import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';
import { useApi } from '../../services/api/ApiProvider';
import { createDeck as apiCreateDeck, updateDeck as apiUpdateDeck } from '../../services/api/apiService';

import DeckEditorManager from '../../features/decks/managers/DeckEditorManager';
import DecksManager from '../../features/decks/managers/decksManager';
import LoadingSpinnerManager from '../../features/common/managers/LoadingSpinnerManager';

function findCardById(cards, id) {
  if (!cards || id == null) return null;
  return cards.find((card) => card.id === id) || null;
}

const DeckEditorPage = ({ mode }) => {
  const { app } = usePixiApp();
  const pixiContainerRef = useRef(null);
  const deckEditorManagerRef = useRef(null);
  const loadingSpinnerManagerRef = useRef(null);

  const navigate = useNavigate();
  const { deckId } = useParams();
  const { decks, fetchDecks, collection, fullCollection } = useApi();

  const saveDeck = async (name, leader, deckCards) => {
    const cards = Object.entries(deckCards).map(([id, { count }]) => ({
      id,
      count,
    }));

    name = name || `Deck-${Math.random().toString(36).substring(2, 8)}`;

    loadingSpinnerManagerRef.current.attachToPixiApp(app);

    if (mode === 'create') {
      await apiCreateDeck(name, leader, cards);
    } else if (mode === 'edit') {
      await apiUpdateDeck(deckId, name, leader, cards);
    }
    const updatedDecks = await fetchDecks();
    await DecksManager.loadDecksAssets(updatedDecks || []);
    navigate(-1);
  };

  const cancel = async () => {
    navigate(-1);
  };

  const onChangeDeckNameClicked = async (currentName) => {
    let newName = await prompt('Enter new deck name (max 20 characters)', currentName);
    newName = newName?.trim();
    if (!newName) return;
    if (newName.length > 20) newName = newName.substring(0, 20);
    deckEditorManagerRef.current.setName(newName);
  };

  useEffect(() => {
    const initPage = async () => {
      if (app && pixiContainerRef.current) {
        pixiContainerRef.current.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
        
        if (mode === 'create') {
          deckEditorManagerRef.current = new DeckEditorManager(mode);
        } else if (mode === 'edit') {
          const deck = decks.find(deck => deck.id === deckId);
          deckEditorManagerRef.current = new DeckEditorManager(mode, !!deck?.isPrebuilt);
        }
        loadingSpinnerManagerRef.current = new LoadingSpinnerManager(app.screen.width, app.screen.height);

        deckEditorManagerRef.current.attachToPixiApp(app);
        pixiContainerRef.current.appendChild(app.canvas);
        
        deckEditorManagerRef.current.buildCreateNewDeckPage();
        await DeckEditorManager.loadAssets();
  
        deckEditorManagerRef.current.onSaveDeck = saveDeck;
        deckEditorManagerRef.current.onCancel = cancel;
        deckEditorManagerRef.current.onChangeDeckNameClicked = onChangeDeckNameClicked;
      }
    }

    initPage();

    return () => {
      if (deckEditorManagerRef.current && app) {
        deckEditorManagerRef.current.detachFromPixiApp(app);
      }

      if (loadingSpinnerManagerRef.current && app) {
        loadingSpinnerManagerRef.current.detachFromPixiApp(app);
      }
    };
  }, [app, pixiContainerRef]);

  useEffect(() => {
    if (deckEditorManagerRef.current && collection) {
      if (mode === 'create') {
        deckEditorManagerRef.current.setMode('leaderSelection');
        deckEditorManagerRef.current.setCollection(collection);
      } else if (mode === 'edit') {
        const deck = decks.find(deck => deck.id === deckId);
        if (!deck) return;

        const resolveCard = (id) => findCardById(collection, id) || findCardById(fullCollection, id);
        const leader = resolveCard(deck.leader);
        const deckCardEntries = deck.cards || [];
        const unresolved = !leader || deckCardEntries.some((entry) => !resolveCard(entry.id));
        if (unresolved && !fullCollection) return;

        const deckCards = deckCardEntries.reduce((acc, entry) => {
          const card = resolveCard(entry.id);
          if (!card) return acc;
          acc[entry.id] = { count: entry.count, card };
          return acc;
        }, {});

        deckEditorManagerRef.current.setMode('addingCardsToDeck');
        deckEditorManagerRef.current.setName(deck.name);
        deckEditorManagerRef.current.setLeader(leader);
        deckEditorManagerRef.current.setDeckCards(deckCards);
        deckEditorManagerRef.current.setCollection(collection);
      }
    }
  }, [collection, fullCollection, decks, deckId, mode]);

  return (
    <div style={{ width: '100dvw', height: '100dvh', overflow: 'hidden' }} ref={pixiContainerRef} />
  );
};

export default DeckEditorPage; 